/**
 * BIGWA Core - Common Types
 * 
 * Tipos compartilhados utilizados em toda a aplicação.
 */

// ============================================================================
// IDS E TIMESTAMPS
// ============================================================================

/**
 * ID único - sempre string UUID
 */
export type UniqueId = string;

/**
 * Timestamp ISO 8601
 */
export type ISOTimestamp = string;

/**
 * Data no formato YYYY-MM-DD
 */
export type DateString = string;

/**
 * Hora no formato HH:MM
 */
export type TimeString = string;

// ============================================================================
// PAGINAÇÃO
// ============================================================================

/**
 * Parâmetros de paginação
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  cursor?: string;
}

/**
 * Resposta paginada
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
    nextCursor?: string;
  };
}

// ============================================================================
// ORDENAÇÃO E FILTROS
// ============================================================================

/**
 * Direção de ordenação
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Parâmetro de ordenação
 */
export interface SortParam<T extends string = string> {
  field: T;
  direction: SortDirection;
}

/**
 * Operadores de filtro
 */
export type FilterOperator = 
  | 'eq'       // igual
  | 'neq'      // diferente
  | 'gt'       // maior que
  | 'gte'      // maior ou igual
  | 'lt'       // menor que
  | 'lte'      // menor ou igual
  | 'contains' // contém (string)
  | 'startsWith'
  | 'endsWith'
  | 'in'       // está em array
  | 'notIn';   // não está em array

/**
 * Filtro genérico
 */
export interface Filter<T extends string = string> {
  field: T;
  operator: FilterOperator;
  value: unknown;
}

// ============================================================================
// ESTADOS DE LOADING
// ============================================================================

/**
 * Estados possíveis de carregamento
 */
export type LoadingState = 'idle' | 'loading' | 'success' | 'error';

/**
 * Estado de uma operação assíncrona
 */
export interface AsyncState<T, E = Error> {
  status: LoadingState;
  data: T | null;
  error: E | null;
}

/**
 * Cria estado inicial
 */
export const createInitialAsyncState = <T>(): AsyncState<T> => ({
  status: 'idle',
  data: null,
  error: null,
});

// ============================================================================
// SYNC STATUS
// ============================================================================

/**
 * Status de sincronização com Google Drive
 */
export type SyncStatus = 
  | 'synced'     // Sincronizado
  | 'syncing'    // Salvando...
  | 'pending'    // Offline / Pendente
  | 'error';     // Erro de sync

/**
 * Informações detalhadas de sync
 */
export interface SyncInfo {
  status: SyncStatus;
  lastSyncedAt?: ISOTimestamp;
  pendingChanges: number;
  error?: string;
}

// ============================================================================
// ENTIDADE BASE
// ============================================================================

/**
 * Campos comuns a todas as entidades
 */
export interface BaseEntity {
  id: UniqueId;
  createdAt: ISOTimestamp;
  updatedAt: ISOTimestamp;
}

/**
 * Entidade com soft delete
 */
export interface SoftDeletableEntity extends BaseEntity {
  deletedAt?: ISOTimestamp;
  isDeleted: boolean;
}

// ============================================================================
// CONFIGURAÇÕES
// ============================================================================

/**
 * Configurações de retry
 */
export interface RetryConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

/**
 * Configuração padrão de retry
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
};

/**
 * Configurações de rate limiting
 */
export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

/**
 * Configurações de cache
 */
export interface CacheConfig {
  ttlMs: number;
  maxEntries?: number;
}

/**
 * TTLs padrão em milissegundos
 */
export const CACHE_TTL = {
  SHORT: 1 * 60 * 1000,        // 1 minuto
  MEDIUM: 5 * 60 * 1000,       // 5 minutos
  LONG: 30 * 60 * 1000,        // 30 minutos
  HOUR: 60 * 60 * 1000,        // 1 hora
  DAY: 24 * 60 * 60 * 1000,    // 1 dia
} as const;

// ============================================================================
// HTTP
// ============================================================================

/**
 * Métodos HTTP suportados
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Headers HTTP
 */
export type HttpHeaders = Record<string, string>;

/**
 * Query params
 */
export type QueryParams = Record<string, string | number | boolean | undefined>;

/**
 * Opções de request HTTP
 */
export interface HttpRequestOptions {
  headers?: HttpHeaders;
  params?: QueryParams;
  timeout?: number;
  signal?: AbortSignal;
}

// ============================================================================
// CALLBACKS E HANDLERS
// ============================================================================

/**
 * Callback genérico
 */
export type Callback<T = void> = (value: T) => void;

/**
 * Callback assíncrono
 */
export type AsyncCallback<T = void> = (value: T) => Promise<void>;

/**
 * Função de cleanup
 */
export type CleanupFn = () => void;

/**
 * Subscriber com unsubscribe
 */
export interface Subscription {
  unsubscribe: CleanupFn;
}

// ============================================================================
// UTILIDADES DE TIPO
// ============================================================================

/**
 * Torna campos específicos opcionais
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Torna campos específicos obrigatórios
 */
export type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/**
 * Tipo para criar entidade (sem id, createdAt, updatedAt)
 */
export type CreateInput<T extends BaseEntity> = Omit<T, keyof BaseEntity>;

/**
 * Tipo para atualizar entidade (todos campos opcionais exceto id)
 */
export type UpdateInput<T extends BaseEntity> = Partial<Omit<T, 'id' | 'createdAt'>> & { id: UniqueId };

/**
 * Tipo não-nulo
 */
export type NonNullable<T> = T extends null | undefined ? never : T;

/**
 * Extrai o tipo de Promise
 */
export type Awaited<T> = T extends Promise<infer U> ? U : T;

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Gera UUID v4
 */
export const generateId = (): UniqueId => {
  return crypto.randomUUID();
};

/**
 * Gera timestamp ISO atual
 */
export const now = (): ISOTimestamp => {
  return new Date().toISOString();
};

/**
 * Converte Date para DateString
 */
export const toDateString = (date: Date): DateString => {
  return date.toISOString().split('T')[0];
};

/**
 * Converte Date para TimeString
 */
export const toTimeString = (date: Date): TimeString => {
  return date.toTimeString().slice(0, 5);
};
